import React, { useState, useEffect, useCallback } from 'react';
import { Eye, RefreshCw, Loader2, History, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { UnifiedAvatar } from '@/components/shared/UnifiedAvatar';

interface CompatibilityLogEntry {
  id: string;
  user_id: string;
  target_user_id: string;
  compatibility_score: number;
  ai_summary: string | null;
  common_points: string[] | null;
  differences: string[] | null;
  ai_model: string | null;
  created_at: string | null;
  evaluator_username?: string;
  evaluator_photo?: string;
  evaluated_username?: string;
  evaluated_photo?: string;
}

const LogEntryDetails: React.FC<{ entry: CompatibilityLogEntry }> = ({ entry }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-1">Score</div>
        <div className="text-2xl font-bold text-luxury-pinkBright">{entry.compatibility_score}%</div>
      </div>
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-1">Modelo IA</div>
        <div className="text-lg font-medium text-white">{entry.ai_model || 'N/A'}</div>
      </div>
    </div>

    <div className="bg-white/5 rounded-lg p-3">
      <div className="text-xs text-white/60 mb-1">Data/Hora</div>
      <div className="text-white">
        {entry.created_at ? format(new Date(entry.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : 'N/A'}
      </div>
    </div>

    {entry.ai_summary && (
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-2">Resumo IA</div>
        <p className="text-white text-sm">{entry.ai_summary}</p>
      </div>
    )}

    {entry.common_points && entry.common_points.length > 0 && (
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
        <div className="text-xs text-green-400 mb-2">Pontos em comum</div>
        <ul className="space-y-1">
          {entry.common_points.map((point, i) => (
            <li key={i} className="text-white text-sm flex items-start gap-2">
              <span className="text-green-400">•</span>
              {point}
            </li>
          ))}
        </ul>
      </div>
    )}

    {entry.differences && entry.differences.length > 0 && (
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
        <div className="text-xs text-orange-400 mb-2">Diferenças</div>
        <ul className="space-y-1">
          {entry.differences.map((diff, i) => (
            <li key={i} className="text-white text-sm flex items-start gap-2">
              <span className="text-orange-400">•</span>
              {diff}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const CompatibilityLogsTab: React.FC = () => {
  const [entries, setEntries] = useState<CompatibilityLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async (reset = false) => {
    setIsLoading(true);
    try {
      const currentPage = reset ? 0 : page;
      const from = currentPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await supabase
        .from('profile_compatibility')
        .select(`
          *,
          evaluator:profiles!profile_compatibility_user_id_fkey(username, main_photo_url),
          evaluated:profiles!profile_compatibility_target_user_id_fkey(username, main_photo_url)
        `)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      const formattedEntries: CompatibilityLogEntry[] = (data || []).map((entry: any) => ({
        id: entry.id,
        user_id: entry.user_id,
        target_user_id: entry.target_user_id,
        compatibility_score: entry.compatibility_score,
        ai_summary: entry.ai_summary,
        common_points: entry.common_points,
        differences: entry.differences,
        ai_model: entry.ai_model,
        created_at: entry.created_at,
        evaluator_username: entry.evaluator?.username,
        evaluator_photo: entry.evaluator?.main_photo_url,
        evaluated_username: entry.evaluated?.username,
        evaluated_photo: entry.evaluated?.main_photo_url,
      }));

      if (reset) {
        setEntries(formattedEntries);
        setPage(0);
      } else {
        setEntries(prev => [...prev, ...formattedEntries]);
      }

      setHasMore(formattedEntries.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching compatibility logs:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchLogs(true);
  }, []);

  const handleRefresh = () => {
    fetchLogs(true);
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    fetchLogs(false);
  };

  // Filter entries by search term
  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.evaluator_username?.toLowerCase().includes(search) ||
      entry.evaluated_username?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header with search and refresh */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Buscar por username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/40"
          />
        </div>
        <Button
          onClick={handleRefresh}
          disabled={isLoading}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Atualizar
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-xs text-white/60">Total de Avaliações</div>
            <div className="text-2xl font-bold text-white">{entries.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-xs text-white/60">Média de Score</div>
            <div className="text-2xl font-bold text-luxury-pinkBright">
              {entries.length > 0 
                ? Math.round(entries.reduce((acc, e) => acc + e.compatibility_score, 0) / entries.length)
                : 0}%
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-xs text-white/60">Mais recente</div>
            <div className="text-sm font-medium text-white">
              {entries[0]?.created_at 
                ? formatDistanceToNow(new Date(entries[0].created_at), { addSuffix: true, locale: ptBR })
                : 'N/A'
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Avaliações ({filteredEntries.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-luxury-pinkBright" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              Nenhuma avaliação encontrada
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Data</th>
                      <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Avaliador</th>
                      <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Avaliado</th>
                      <th className="text-center py-3 px-2 text-xs text-white/60 font-medium">Score</th>
                      <th className="text-right py-3 px-2 text-xs text-white/60 font-medium">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-2">
                          <div className="text-white text-sm">
                            {entry.created_at 
                              ? format(new Date(entry.created_at), "dd/MM/yy", { locale: ptBR })
                              : 'N/A'
                            }
                          </div>
                          <div className="text-white/40 text-xs">
                            {entry.created_at 
                              ? format(new Date(entry.created_at), "HH:mm", { locale: ptBR })
                              : ''
                            }
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <UnifiedAvatar
                              src={entry.evaluator_photo}
                              alt={entry.evaluator_username || ''}
                              size="small"
                              fallback={entry.evaluator_username?.charAt(0) || '?'}
                            />
                            <span className="text-white text-sm">{entry.evaluator_username || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <UnifiedAvatar
                              src={entry.evaluated_photo}
                              alt={entry.evaluated_username || ''}
                              size="small"
                              fallback={entry.evaluated_username?.charAt(0) || '?'}
                            />
                            <span className="text-white text-sm">{entry.evaluated_username || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`font-bold ${
                            entry.compatibility_score >= 70 ? 'text-green-400' :
                            entry.compatibility_score >= 40 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {entry.compatibility_score}%
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-gray-900 border-white/20 max-w-2xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="text-white flex items-center gap-2">
                                  <span>{entry.evaluator_username}</span>
                                  <span className="text-white/40">→</span>
                                  <span>{entry.evaluated_username}</span>
                                </DialogTitle>
                              </DialogHeader>
                              <LogEntryDetails entry={entry} />
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Load more button */}
              {hasMore && !searchTerm && (
                <div className="flex justify-center mt-4">
                  <Button
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Carregar mais
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
